import { useAdminAuthStore } from "@/stores/admin-auth-store";
import type { IAdminRole } from "@/services/admin-management-service";

/**
 * Module keys for the admin permission system.
 * Mirrors backend `app.permissions.MODULES`.
 */
export type AdminModule =
  | "dashboard"
  | "user_management"
  | "agent_management"
  | "project_management"
  | "inquiry_management"
  | "ax_trend_hub"
  | "admin_management";

/** Action keys for the admin permission system. */
export type PermissionAction = "read" | "create" | "update" | "delete";

export type PermissionMatrix = Record<AdminModule, PermissionAction[]>;

/**
 * Role × Module × Action matrix.
 * Single source of truth for the admin UI; mirrors backend.
 *
 * - Administrator: full access to every module
 * - Editor: read/create/update on Users, Project, Agents (no delete)
 * - Viewer: dashboard read only
 */
export const PERMISSION_MATRIX: Record<IAdminRole, PermissionMatrix> = {
  administrator: {
    dashboard: ["read"],
    user_management: ["read", "create", "update", "delete"],
    agent_management: ["read", "create", "update", "delete"],
    project_management: ["read", "create", "update", "delete"],
    inquiry_management: ["read", "create", "update", "delete"],
    ax_trend_hub: ["read", "create", "update", "delete"],
    admin_management: ["read", "create", "update", "delete"],
  },
  editor: {
    dashboard: [],
    user_management: ["read", "create", "update"],
    agent_management: ["read", "create", "update"],
    project_management: ["read", "create", "update"],
    inquiry_management: [],
    ax_trend_hub: [],
    admin_management: [],
  },
  viewer: {
    dashboard: ["read"],
    user_management: [],
    agent_management: [],
    project_management: [],
    inquiry_management: [],
    ax_trend_hub: [],
    admin_management: [],
  },
};

/** Pure helper to check a single permission. */
export const hasPermissionByRole = (
  role: IAdminRole | null | undefined,
  module: AdminModule,
  action: PermissionAction,
): boolean => {
  if (!role) return false;
  return PERMISSION_MATRIX[role][module]?.includes(action) ?? false;
};

/**
 * React hook that exposes the current admin's permission helpers.
 * Reads the role from the auth store; returns sensible defaults while
 * unauthenticated (all checks return false).
 */
export const usePermissions = () => {
  const admin = useAdminAuthStore((s) => s.admin);
  const role = (admin?.role as IAdminRole | undefined) ?? null;

  const hasPermission = (module: AdminModule, action: PermissionAction) =>
    hasPermissionByRole(role, module, action);

  const can = {
    read: (module: AdminModule) => hasPermission(module, "read"),
    create: (module: AdminModule) => hasPermission(module, "create"),
    update: (module: AdminModule) => hasPermission(module, "update"),
    delete: (module: AdminModule) => hasPermission(module, "delete"),
  };

  const permissions: PermissionMatrix = role
    ? PERMISSION_MATRIX[role]
    : (Object.fromEntries(
        [
          "dashboard",
          "user_management",
          "agent_management",
          "project_management",
          "inquiry_management",
          "ax_trend_hub",
          "admin_management",
        ].map((m) => [m, [] as PermissionAction[]]),
      ) as PermissionMatrix);

  return { role, hasPermission, can, permissions };
};
